/*
 * Imperative confirm() replacement using shadcn AlertDialog.
 *
 * Usage:
 *   import { confirm } from "@/lib/confirm";
 *   if (!(await confirm({ title: "حذف الوصفة نهائياً؟", danger: true }))) return;
 *
 * Falls back to native `window.confirm` if the ConfirmHost is not mounted
 * (e.g. server-render), so existing call-sites remain safe.
 *
 * Options:
 *   title       — headline (bold, big)
 *   message     — body (multi-line ok — `\n` preserved)
 *   okText      — primary button label
 *   cancelText  — secondary button label
 *   danger      — red destructive treatment (warning icon, red header, red button)
 */
import { createRoot } from "react-dom/client";
import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

let _resolve = null;

function ConfirmHost() {
  const [state, setState] = useState({ open: false, opts: {} });
  const [typedValue, setTypedValue] = useState("");
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    window.__calofayConfirm = (opts) => new Promise((res) => {
      _resolve = res;
      setTypedValue("");
      setState({ open: true, opts: opts || {} });
    });
    return () => { delete window.__calofayConfirm; };
  }, []);

  const finish = (ok) => {
    setState((s) => ({ ...s, open: false }));
    setTypedValue("");
    if (_resolve) { _resolve(ok); _resolve = null; }
  };

  const { opts } = state;
  const danger = !!opts.danger;
  // Read the app language directly from storage — the confirm host
  // renders at the app root, above any i18n provider that would give
  // us a hook. Falls back to Arabic for legacy behaviour.
  const lang = (() => {
    try { return localStorage.getItem("calofay:lang") || "ar"; }
    catch { return "ar"; }
  })();
  const en = lang === "en";
  const dir = en ? "ltr" : "rtl";
  const textAlign = en ? "text-left" : "text-right";

  return (
    <AlertDialog open={state.open} onOpenChange={(v) => !v && finish(false)}>
      <AlertDialogContent
        data-testid="confirm-dialog"
        className={`always-light w-[calc(100vw-2rem)] max-w-sm p-0 overflow-hidden ${danger ? "border-2 border-red-400 shadow-[0_20px_60px_-15px_rgba(239,68,68,0.45)]" : ""}`}
      >
        <div dir={dir}>
        {/* Danger banner — big red warning strip with pulsing alert icon */}
        {danger && (
          <div className="relative overflow-hidden">
            <div className="bg-gradient-to-b from-red-500 to-red-600 px-4 py-3 flex items-center gap-3 text-white">
              <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 animate-pulse">
                <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className={`${textAlign} flex-1 min-w-0`}>
                <div className="text-[9px] font-black tracking-wider opacity-85 leading-tight">
                  {en ? "⚠ WARNING · CANNOT BE UNDONE" : "⚠ تحذير · لا يمكن التراجع"}
                </div>
                <div className="text-sm font-black leading-tight mt-1">
                  {en ? "Read before continuing" : "انتبه قبل المتابعة"}
                </div>
              </div>
            </div>
            <div className="h-1.5 bg-[repeating-linear-gradient(45deg,#facc15_0_8px,#1f2937_8px_16px)]" />
          </div>
        )}

        <div className="p-5 space-y-4">
          <AlertDialogHeader className="space-y-2">
            <AlertDialogTitle
              className={`${textAlign} ${danger ? "text-red-700 text-base font-black" : ""}`}
              data-testid="confirm-title"
            >
              {opts.title || (en ? "Confirm" : "تأكيد")}
            </AlertDialogTitle>
            {(opts.message || opts.description) && (
              <AlertDialogDescription
                className={`${textAlign} whitespace-pre-wrap leading-relaxed text-[12px] ${danger ? "text-neutral-700 border-r-4 border-red-300 pr-3 bg-red-50/50 py-2 rounded" : ""}`}
                data-testid="confirm-message"
              >
                {opts.message || opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>

          {opts.requireInput && (
            <div className="pt-1">
              <input
                type="text"
                autoFocus
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                placeholder={en ? `Type: ${opts.requireInput}` : `اكتب: ${opts.requireInput}`}
                className={`w-full bg-white border-2 border-red-300 rounded-xl px-3 py-2 ${textAlign} text-sm font-bold focus:outline-none focus:border-red-500`}
                data-testid="confirm-require-input"
                dir={dir}
              />
              <div className={`text-[10px] text-red-500 mt-1 ${textAlign}`}>
                {en ? "Type the exact word to confirm" : "اكتب الكلمة بالضبط للتأكيد"}
              </div>
            </div>
          )}

          <AlertDialogFooter className="flex-col-reverse sm:flex-row-reverse gap-2">
            <AlertDialogAction
              onClick={() => finish(true)}
              data-testid="confirm-ok"
              disabled={!!opts.requireInput && typedValue.trim() !== opts.requireInput}
              className={`${danger
                ? "bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-500/30 active:scale-95 transition-transform text-[11px] px-3"
                : "bg-brand-500 hover:bg-brand-600 text-white"} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {opts.okText || opts.confirmLabel || (en ? (danger ? "Yes, delete" : "Confirm") : (danger ? "نعم، احذف" : "تأكيد"))}
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => finish(false)}
              data-testid="confirm-cancel"
              className={danger ? "border-2 border-neutral-300 font-bold text-[11px]" : ""}
            >
              {opts.cancelText || opts.cancelLabel || (en ? "Cancel" : "إلغاء")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Mount the host once on module load
if (typeof window !== "undefined") {
  const host = document.createElement("div");
  host.id = "calofay-confirm-host";
  document.body.appendChild(host);
  createRoot(host).render(<ConfirmHost />);
}

export function confirm(opts) {
  if (typeof window === "undefined" || !window.__calofayConfirm) {
    return Promise.resolve(window.confirm(opts?.title || opts?.message || "؟"));
  }
  return window.__calofayConfirm(typeof opts === "string" ? { title: opts } : opts);
}
